<script lang="ts">
  import Card from '@smui/card';
  import type { Options } from '@splidejs/splide';
  import '@splidejs/splide/dist/css/themes/splide-default.min.css';
  import { Splide, SplideSlide } from '@splidejs/svelte-splide';
  import { createEventDispatcher } from 'svelte';
  import { isDirectionRTL, isMobile } from '../../../common/util';
  import Icon from '../../common/components/Icon.svelte';
  import Text from '../../common/components/Text.svelte';
  import { getImageAltText, getStyle } from '../services/pdp-data-services';
  import { hightlightDetailsClickOnSlideIndex } from '../store/pdp_store';

  export let keyHighlights;
  export let visible;

  let storySlider: Splide;
  let highLightSlider: Splide;
  let progress;
  let progressRatio = 0;
  const dispatch = createEventDispatcher();
  const isRTL = isDirectionRTL();
  let activeSlide = 0;
  const mobile = isMobile();
  const sliderOption: Options = {
    type: 'loop',
    direction: isRTL ? 'rtl' : 'ltr',
    autoplay: true,
    pauseOnFocus: false,
    pauseOnHover: true,
    arrows: false,
    pagination: false,
    resetProgress: false,
    perPage: 1,
    perMove: 1,
    breakpoints: {
      480: {
        gap: 8,
        pagination: false,
        wheel: false,
        perPage: 1,
        perMove: 1,
      },
    },
  };

  const highLightSliderOption: Options = {
    type: 'slide',
    direction: isRTL ? 'rtl' : 'ltr',
    pagination: false,
    gap: '4px',
    fixedHeight: '100%',
    fixedWidth: 156,
    isNavigation: true,
    releaseWheel: true,
    cover: true,
    updateOnMove: true,
    padding: isRTL ? { right: 41 } : { left: 41 },
    perMove: 1,
    classes: {
      arrow: 'splide__arrow splide_arrow-ps',
      prev: 'splide__arrow--prev splide_arrow-prev',
      next: 'splide__arrow--next splide_arrow-next',
    },
    breakpoints: {
      900: {
        width: '100vw',
        fixedWidth: 102,
        fixedHeight: '100%',
        gap: 8,
        padding: { left: 16, right: 16 },
      },
    },
  };

  $: if (visible) {
    progressBar();
    caraousalBar();
    if (storySlider && highLightSlider) {
      storySlider.splide.mount();
      highLightSlider.splide.mount();
      highLightSlider.splide.sync(storySlider.splide);
      setTimeout(() => {
        storySlider.splide.refresh();
        highLightSlider.go($hightlightDetailsClickOnSlideIndex);
      }, 250);
    }
  } else {
    if (storySlider && highLightSlider) {
      storySlider.splide.destroy();
      highLightSlider.splide.destroy();
    }
  }

  function progressBar() {
    storySlider?.splide.event.on('autoplay:playing', function (rate) {
      activeSlide = storySlider.splide.index;
      progressRatio = rate;
    });
    // storySlider?.splide.event.on('active', (slide) => {
    //     highLightSlider.splide.refresh();
    // });
  }

  function caraousalBar() {
    const bar: any = highLightSlider?.splide.root.nextElementSibling.querySelector('.my-carousel-progress-bar');

    highLightSlider?.splide.on(['mounted', 'active'], function () {
      const end = highLightSlider.splide.Components.Controller.getEnd() + 1;
      const rate = Math.min((highLightSlider.splide.index + 1) / end, 1);
      bar.style.width = String(100 * rate) + '%';
    });
  }
</script>

<div class="highlight-details-slider">
  <div class="progress-container hide-on-desktop">
    {#each keyHighlights as _s, i}
      <div class="splide__progress">
        <div
          style:transform={activeSlide < i ? 'scaleX(0)' : activeSlide === i ? `scaleX(${progressRatio})` : 'scaleX(1)'}
          class="splide__progress__bar"
          bind:this={progress}
        ></div>
      </div>
    {/each}
  </div>
  <div class="hide-on-mobile splide__progress">
    <div style:transform={`scaleX(${progressRatio})`} class="splide__progress__bar" bind:this={progress}></div>
  </div>
  <Splide
    id="autoplay"
    class="highlight-main-slider"
    bind:this={storySlider}
    hasTrack={false}
    options={{ ...sliderOption }}
  >
    {#each keyHighlights as slide}
      <SplideSlide>
        <Card class="info-card">
          {#if slide.keyDetailsImage !== null}
            {#if mobile && slide.keyDetailsImageForMobile !== null}
              <picture>
                <source type="image/webp" srcset={slide.keyDetailsImageForMobile + '?fmt=webp'} />
                <source type="image/jpeg" srcset={slide.keyDetailsImageForMobile + '?fmt=jpeg'} />
                <img class="key-details-image" src={slide.keyDetailsImageForMobile} alt="" />
              </picture>
            {:else}
              <picture>
                <source type="image/webp" srcset={slide.keyDetailsImage + '?fmt=webp'} />
                <source type="image/jpeg" srcset={slide.keyDetailsImage + '?fmt=jpeg'} />
                <img class="key-details-image" src={slide.keyDetailsImage} alt="" />
              </picture>
            {/if}
          {:else if slide.keyDetailsHtml !== null}
            <span>
              {@html slide?.keyDetailsHtml}
            </span>
          {/if}
        </Card>
      </SplideSlide>
    {/each}
  </Splide>

  <div class="small-slider" class:rtl-btn={isRTL}>
    <Splide options={{ ...highLightSliderOption }} bind:this={highLightSlider}>
      {#each keyHighlights as slide}
        <SplideSlide>
          <div class="splide__slide__container">
            <div class="card-container">
              <Card class="small-card" style={`background-color:${slide.background};`}>
                {#if slide.backgroundImage}
                  <img
                    class="card-background-image"
                    src={slide.backgroundImage}
                    alt={getImageAltText(slide.backgroundImage)}
                  />
                {/if}
                <div class="inner-details">
                  {#if slide?.icon}
                    <img src={slide?.icon} alt="" />
                  {/if}
                  {#if slide?.title}
                    <div class="mdc-typography--headline6" style={getStyle(slide?.titleColorCode, 'title')}>
                      {slide?.title}
                    </div>
                  {/if}
                  {#if slide?.description}
                    <div class="mdc-typography--caption" style={getStyle(slide?.descriptionColorCode, 'description')}>
                      {slide?.description}
                    </div>
                  {/if}
                </div>
              </Card>
            </div>
          </div>
        </SplideSlide>
      {/each}
    </Splide>
    <div class="my-carousel-progress">
      <div class="my-carousel-progress-bar"></div>
    </div>
  </div>
  <!-- svelte-ignore a11y-invalid-attribute -->
  <a href="javascript:void(0)" on:click={() => dispatch('close')} class="geek-out">
    <Text key="extra.geekInfoLableOne" />
    {#if isRTL}
      <Icon type="font" desktopSize={20} glyph="chevron_down" glyphDesktop="chevron_down" />
    {:else}
      <Icon type="font" desktopSize={20} glyph="chevron_right" glyphDesktop="chevron_right" />
    {/if}
  </a>
</div>

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  .highlight-details-slider {
    .mdc-typography--caption {
      font-weight: 400;
      font-size: 11px;
      line-height: 12px;
      margin: 5px 0;
    }

    .c_icon {
      transform: rotate(180deg);
      margin-left: 12px;
      font-size: 11px;
    }
    .geek-out {
      text-align: center;
      display: flex;
      gap: 12px;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      color: $primary-white;
      margin: 18px 0;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      text-decoration: none;

      @include desktop-min-width {
        font-size: 16px;
        line-height: 18px;
        margin: 40px 0 39px 0;
      }
    }
    .mdc-typography--headline6 {
      font-weight: 700;
      font-size: 14px;
      line-height: 16px;
      margin: 5px 0;
    }

    .progress-container {
      display: flex;
      gap: 10px;
      align-items: center;
      justify-content: center;
      margin: 0 50px 0 16px;
    }
    .splide__progress {
      background: rgba(219, 227, 232, 0.2);
      border-radius: 9px;
      margin: 0 auto 26px auto;
      width: 100%;

      @include desktop-min-width {
        width: 500px;
        border-radius: 9px;
        margin: 0 auto 20px auto;
      }
    }

    .splide__progress__bar {
      background: $neutral-dark-04;
      height: 4px;
      transition: transform 100ms ease;
      transform-origin: left center;
      width: 100%;

      @include desktop-min-width {
        background: $neutral-dark-04;
        height: 4px;
        transition: width 400ms ease;
      }
    }

    .button-close {
      position: absolute;
      right: 0;
      height: 20px;
      width: 20px;
    }
  }

  :global {
    .highlight-details-slider {
      .small-slider.rtl-btn .splide__arrow {
        // transform: translateY(-50%) scaleX(-1);
      }

      .small-slider {
        max-width: calc(100vw - 48%);
        .splide__arrow svg {
          fill: $color-yellow;
        }

        .splide_arrow-prev {
          @include desktop-min-width {
            left: 48px !important;
            border: 1px solid $neutral-dark-05 !important;
            svg {
              fill: $neutral-dark-05;
            }
          }
        }
        .splide_arrow-next {
          @include desktop-min-width {
            left: 120px !important;
          }
        }

        .splide__arrow {
          @include desktop-min-width {
            width: 56px;
            height: 56px;
            padding: 5px;
            top: 130%;
          }
        }

        .splide {
          .splide__track {
            .splide__list {
              .splide__slide {
                @include desktop-min-width {
                  width: 156px;
                  height: 134px;
                }
                .splide__slide__container {
                  @include desktop-min-width {
                    height: 118px;
                    width: 140px;
                  }
                }
              }
            }
          }
        }
        .splide--rtl {
          direction: unset;
        }
      }
      @media all and (max-width: 900px) {
        .highlight-main-slider {
          .splide__track {
            height: 55vh;
          }
          .splide__track .info-card,
          .splide__track .info-card img {
            height: 100%;
            width: fit-content;
          }
        }
        .small-slider {
          margin-top: 16px;
          max-width: 100vw;

          .splide__arrows {
            display: none;
          }

          .small-card,
          .card-container {
            height: 100%;
          }
          .inner-details {
            padding: 10px;
          }
        }

        .my-carousel-progress {
          background: rgba(219, 227, 232, 0.09);
          // margin: 36.5px 0 40px 0;
          border-radius: 100px;
          margin: 35.5px 16px 0 16px;
          position: relative;
        }

        .my-carousel-progress-bar {
          background: $color-yellow;
          height: 8px;
          transition: width 100ms ease;
          width: 0;
          border-radius: 4px;
        }
      }
      .mdc-dialog__header {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        margin: 20px;
      }

      .small-slider .splide__slide {
        &.is-active {
          border: 1px solid $color-yellow !important;
          border-radius: 12px;
        }
      }

      .mdc-dialog__content {
        padding: 0 16px !important;
      }
      .mdc-dialog__content::-webkit-scrollbar {
        display: none;
      }

      .small-card {
        border-radius: 12px !important;
        height: 118px;
      }

      .pdp-story-dialog {
        z-index: 9999 !important;
      }

      .info-image {
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }

      .info-card {
        align-items: center;
        border-radius: 20px !important;
        justify-content: center;
        margin: 0 auto 20px auto;
        background-color: inherit;
        img {
          border-radius: 20px;
          height: 53.5vh;
          max-height: 53.5vh;
          object-fit: cover;
          width: 100%;
          @include desktop-min-width {
            object-fit: contain;
            height: 48vh;
            max-height: 48vh;
          }
        }
      }

      #autoplay.splide--rtl {
        direction: unset;
      }

      .inner-details {
        img {
          width: 30px;
          height: 30px !important;
        }
      }

      @include desktop-min-width {
        .splide__slide__container {
          margin: 8px 7px;
        }

        .button-close {
          height: 30px;
          width: 30px;
        }

        .mdc-typography--caption {
          font-weight: 400;
          font-size: 14px;
          line-height: 16px;
          height: 32px;
        }

        .inner-details {
          padding: 12px;
          img {
            width: 30px;
            height: 30px !important;
          }
        }

        .c_icon {
          transform: rotate(270deg);
          font-size: 20px;
        }

        .my-carousel-progress {
          background: rgba(219, 227, 232, 0.09);
          // margin: 36.5px 0 40px 0;
          border-radius: 100px;
          margin: 35.5px 80px 0 200px;
          position: relative;
        }

        .my-carousel-progress-bar {
          background: $color-yellow;
          height: 8px;
          transition: width 100ms ease;
          width: 0;
          border-radius: 4px;
        }

        .mdc-typography--headline6 {
          font-weight: 700;
          font-size: 20px;
          line-height: 22px;
          height: unset;
        }

        .splide__progress {
          background: rgba(219, 227, 232, 0.2);
          border-radius: 4px;
          margin: 0 auto 20px auto;
        }

        .splide__progress__bar {
          background: $neutral-dark-04;
          height: 4px;
          transition: width 100ms ease;
          width: 100%;
          border-radius: 4px;
        }
      }
    }
  }
  :global(.small-slider .mdc-card) {
    position: relative;
    overflow: hidden;
  }

  .card-background-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
  }

  .small-slider .inner-details {
    position: relative;
    z-index: 1;
  }
</style>
